import styled, {css, keyframes} from 'styled-components'
import get from 'lodash/get'
import isObject from 'lodash/isObject'
import breakpoint from 'components/breakpoint'

const getAttrs = props => {
  const attrs = {}
  if (props.autoComplete) {
    attrs.autoComplete = props.autoComplete
  }

  return attrs
}

const getBackground = props => {
  const defaultBackground = '#fff'

  if (props.error) {
    return props.inputErrorBackground || props.theme.input.errorBackground || defaultBackground
  }

  return props.inputBackgroundColor || props.theme.input.background || defaultBackground
}

const getErrorBorder = props => props.error && props.theme.input.errorBorder
const getErrorBorderBottom = props => {
  if (props.error) {
    return props.theme.input.errorBorderBottom || props.theme.input.errorBorder
  }
}

const getFocusBorder = props => getErrorBorder(props) || props.theme.input.focusBorder
const getFocusBottomBorder = props => getErrorBorderBottom(props) || props.theme.input.focusBorderBottom

const getBorder = props => {
  if (props.focused) {
    return getFocusBorder(props)
  }

  return getErrorBorder(props) || props.inputBorderColor || props.theme.input.border
}
const getBottomBorder = props => {
  if (props.focused) {
    return getFocusBottomBorder(props)
  }

  return getErrorBorderBottom(props) || props.inputBorderBottomColor || props.theme.input.borderBottom
}

const getHoverBorder = props => getErrorBorder(props) || props.theme.input.hoverBorder
const getHoverBottomBorder = props => getErrorBorderBottom(props) || props.theme.input.hoverBorderBottom

const getPadding = props => props.inputPadding || props.theme.input.padding || '8px 16px'
const getFontFamily = props => props.theme.input.fontFamily || props.theme.font.family

const getFontSize = (props, breakpoint) => {
  const inputFontSize = props.theme.input.fontSize
  if (isObject(inputFontSize)) {
    return inputFontSize[breakpoint]
  }
  return inputFontSize || props.theme.font.mobile.size
}

const getBorderTopLeftRadius = props => props.borderTopLeftRadius || props.theme.input.borderTopLeftRadius
const getBorderTopRightRadius = props => props.borderTopRightRadius || props.theme.input.borderTopRightRadius
const getBorderRadius = props => props.borderRadius || props.theme.input.borderRadius || 0

const Input = styled.input.attrs(getAttrs)`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  box-sizing: border-box;
  width: 100%;
  height: ${props => props.theme.input.height};
  color: ${props => props.theme.input.color || '#000000'};
  font-size: ${props => getFontSize(props, 'mobile')};
  font-family: ${getFontFamily};
  padding: ${getPadding};
  border: ${getBorder};
  border-bottom: ${getBottomBorder};
  background: ${getBackground};
  outline: none;
  border-radius: ${getBorderRadius};
  border-top-left-radius: ${getBorderTopLeftRadius};
  border-top-right-radius: ${getBorderTopRightRadius};

  &:hover {
    border: ${getHoverBorder};
    border-bottom: ${getHoverBottomBorder};
  }

  &:focus {
    border: ${getFocusBorder};
    border-bottom: ${getFocusBottomBorder};
  }

  input&:-webkit-autofill,
  input&:-webkit-autofill:hover,
  input&:-webkit-autofill:focus,
  input&:-webkit-autofill:active {
    box-shadow: inset 0 0 0 9999px ${getBackground};
    -webkit-text-fill-color: ${props => props.theme.input.color || '#000000'};
  }

  input&[type='number'] {
    -moz-appearance: textfield;
  }

  input&::-webkit-outer-spin-button,
  input&::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  input&[type='password'] {
    &::-ms-reveal {
      display: none;
    }
    &::-webkit-credentials-auto-fill-button {
      position: absolute;
      right: 78px;
    }
  }

  ${breakpoint('portrait')`
    font-size: ${props => getFontSize(props, 'portrait')};
  `};
`

const TextArea = styled(Input.withComponent('textarea'))`
  height: ${props => props.theme.textarea.height};
  resize: none;
`

const InputView = styled.div`
  position: relative;
`

const getShowPasswordButtonTextDecoration = props => get(props, 'theme.input.password.textDecoration', 'underline')
const getShowPasswordButtonHoverTextDecoration = props =>
  get(props, 'theme.link.textDecorationHover', getShowPasswordButtonTextDecoration(props))

const ShowPasswordButton = styled.div`
  position: absolute;
  top: ${props => props.theme.input.password.top};
  right: ${props => props.theme.input.password.right || '10px'};
  color: ${props => props.theme.input.password.color};
  font-size: ${props => props.theme.input.password.size || props.theme.font.size};
  font-family: ${props => props.theme.input.password.family || 'inherit'};
  line-height: ${props => props.theme.input.password.height || props.theme.font.mobile.height};
  text-align: right;
  text-decoration: ${getShowPasswordButtonTextDecoration};
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.input.password.hoverColor};
    text-decoration: ${getShowPasswordButtonHoverTextDecoration};
  }
`

const rotate = keyframes`
  0% {
      transform-origin: 100% 100%;
      transform: rotate(0deg);
    }
    100% {
      transform-origin: 100% 100%;
      transform: rotate(360deg);
    }
`

const Loading = styled.span`
  margin: 5px 5px 0 0;
  position: absolute;
  top: ${props => props.theme.input.loading?.top || '7px'};
  right: -7px;
  z-index: 2;
  display: block;
  width: 34px;
  height: 34px;
  line-height: 34px;
  text-align: center;
  pointer-events: none;

  &:before {
    position: absolute;
    display: block;
    content: '';
    z-index: 12;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${getBackground};
  }

  &:after {
    position: absolute;
    display: block;
    content: '';
    z-index: 11;
    width: 12px;
    height: 12px;
    border-radius: 200px 0 0;
    background: linear-gradient(45deg, rgba(0, 0, 0, 0) 0, #459ad7 50%, #459ad7 100%);
    animation: ${css`
      ${rotate} 0.5s linear infinite;
    `};
  }
`

const exportedFuncs = {input: {getHoverBorder, getHoverBottomBorder, getFocusBottomBorder}}

export {Input, TextArea, InputView, ShowPasswordButton, Loading, exportedFuncs as funcs}
